import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Warning, ArrowsLeftRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableCombobox from "@/components/SearchableCombobox";

const cname = (c) => c.company_name || c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id;

function buildShares(mode, property, newId) {
  const old = property?.ownership_shares || [];
  if (mode === "keep") return null;
  if (mode === "new100") return [];
  const oldId = property?.owner_contact_id;
  const map = new Map();
  old.forEach((s) => {
    const id = s.contact_id === oldId ? newId : s.contact_id;
    map.set(id, (map.get(id) || 0) + Number(s.share_percent || 0));
  });
  return [...map.entries()].map(([contact_id, share_percent]) => ({ contact_id, share_percent }));
}

function Opt({ id, checked, onChange, children, testId }) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testId} className="mt-0.5" />
      <span>{children}</span>
    </label>
  );
}

function PreviewBlock({ plan }) {
  if (!plan) return null;
  const changed = plan.tenancies.filter((t) => t.new_contract_number || t.new_rent_iban);
  return (
    <div className="border border-stone-200 rounded-md bg-stone-50 p-3 space-y-2 text-xs" data-testid="owner-move-preview">
      {plan.warnings.map((w) => (
        <div key={w} className="flex gap-1.5 text-amber-800"><Warning size={14} className="shrink-0 mt-0.5" />{w}</div>
      ))}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-600">{plan.old_owner.name || "—"}{plan.old_owner.owner_number ? ` #${plan.old_owner.owner_number}` : ""}</span>
        <ArrowRight size={14} />
        <span className="font-semibold text-slate-900">{plan.new_owner.name}{plan.new_owner.owner_number ? ` #${plan.new_owner.owner_number}` : ""}</span>
      </div>
      {plan.bank.will_change && (
        <div data-testid="owner-move-bank-preview">Mietkonto: <span className="font-mono">{plan.bank.old_iban || "—"}</span> → <span className="font-mono font-semibold">{plan.bank.new_iban}</span></div>
      )}
      {changed.length > 0 ? (
        <div className="max-h-48 overflow-auto">
          <table className="w-full">
            <thead><tr className="text-left text-slate-500"><th className="py-1">Vertragsnr. alt</th><th>neu</th><th>SEPA-Mandat neu</th></tr></thead>
            <tbody>
              {plan.tenancies.map((t) => (
                <tr key={t.tenancy_id} className="border-t border-stone-200" data-testid={`owner-move-row-${t.tenancy_id}`}>
                  <td className="py-1 font-mono">{t.old_contract_number || "—"}</td>
                  <td className="font-mono font-semibold">{t.new_contract_number || "unverändert"}</td>
                  <td className="font-mono">{t.new_sepa_reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="text-slate-500">Keine Mietverhältnisse betroffen.</div>}
    </div>
  );
}

export default function OwnerMoveDialog({ open, onOpenChange, property, onDone }) {
  const [owners, setOwners] = useState([]);
  const [f, setF] = useState({});
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    setF({ new_owner_contact_id: "", mode: "correction", effective_date: "", renumber_contracts: true,
      update_sepa_reference: true, update_bank: true,
      shares_mode: (property?.ownership_shares || []).length ? "replace" : "keep", note: "" });
    api.get("/contacts", { params: { limit: 1000 } }).then(({ data }) =>
      setOwners((data || []).filter((c) => (c.roles || []).includes("owner") && c.id !== property?.owner_contact_id)));
  }, [open, property]);

  const payload = useMemo(() => ({
    new_owner_contact_id: f.new_owner_contact_id, mode: f.mode,
    effective_date: f.mode === "sale" ? f.effective_date || null : null,
    renumber_contracts: f.renumber_contracts, update_sepa_reference: f.update_sepa_reference,
    update_bank: f.update_bank, note: f.note,
    ownership_shares: buildShares(f.shares_mode, property, f.new_owner_contact_id),
  }), [f, property]);

  useEffect(() => {
    if (!open || !f.new_owner_contact_id || (f.mode === "sale" && !f.effective_date)) { setPlan(null); return; }
    const t = setTimeout(() => {
      api.post(`/properties/${property.id}/owner-move/preview`, payload)
        .then(({ data }) => setPlan(data))
        .catch((e) => { setPlan(null); toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); });
    }, 250);
    return () => clearTimeout(t);
  }, [open, payload, property, f.new_owner_contact_id, f.mode, f.effective_date]);

  const apply = async () => {
    if (!await window._domivusConfirm("Eigentümer-Umzug jetzt durchführen? Vertragsnummern und Mietkonto werden wie in der Vorschau angepasst.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/owner-move`, payload);
      toast.success(`Eigentümer-Umzug durchgeführt${data.renumbered.length ? ` · ${data.renumbered.length} Vertragsnr. umnummeriert` : ""}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Umzug fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const hasShares = (property?.ownership_shares || []).length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="owner-move-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowsLeftRight size={18} /> Eigentümer-Umzug</DialogTitle>
          <DialogDescription>Maßgeblichen Eigentümer des Objekts wechseln — inkl. Eigentümer-Nummer, Vertragsnummern und Mietkonto.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Neuer maßgeblicher Eigentümer</Label>
            <SearchableCombobox value={f.new_owner_contact_id || ""} onChange={(v) => set("new_owner_contact_id", v)}
              testId="owner-move-new-owner" placeholder="Eigentümer wählen…"
              options={owners.map((c) => ({ id: c.id, label: `${c.owner_number ? `[${c.owner_number}] ` : ""}${cname(c)}`,
                keywords: [c.owner_number, c.first_name, c.last_name, c.company_name].filter(Boolean).join(" ") }))} />
          </div>
          <div>
            <Label>Art des Umzugs</Label>
            <RadioGroup value={f.mode} onValueChange={(v) => set("mode", v)} className="mt-1.5 space-y-1.5">
              <label className="flex items-start gap-2 text-sm cursor-pointer"><RadioGroupItem value="correction" data-testid="owner-move-mode-correction" className="mt-0.5" />
                <span><b>Korrektur</b> — Eigentümer war falsch erfasst, alles rückwirkend umhängen</span></label>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><RadioGroupItem value="sale" data-testid="owner-move-mode-sale" className="mt-0.5" />
                <span><b>Eigentümerwechsel zum Stichtag</b> — Verkauf; Buchungen vor dem Stichtag gelten als Voreigentümer-Zeit</span></label>
            </RadioGroup>
            {f.mode === "sale" && (
              <Input type="date" className="mt-2 w-48" value={f.effective_date || ""} onChange={(e) => set("effective_date", e.target.value)} data-testid="owner-move-effective-date" />
            )}
          </div>
          <div className="space-y-2">
            <Opt id="om-renumber" checked={f.renumber_contracts} onChange={(v) => set("renumber_contracts", v)} testId="owner-move-renumber">
              Vertragsnummern auf die neue Eigentümer-Nummer umnummerieren (alte Nummer bleibt in der Historie)</Opt>
            {f.renumber_contracts && (
              <div className="pl-6"><Opt id="om-sepa" checked={f.update_sepa_reference} onChange={(v) => set("update_sepa_reference", v)} testId="owner-move-sepa">
                SEPA-Mandatsreferenz mit anpassen</Opt></div>
            )}
            <Opt id="om-bank" checked={f.update_bank} onChange={(v) => set("update_bank", v)} testId="owner-move-bank">
              Mietkonto auf das Standardkonto des neuen Eigentümers umstellen</Opt>
          </div>
          <div>
            <Label>Eigentumsquoten</Label>
            <RadioGroup value={f.shares_mode} onValueChange={(v) => set("shares_mode", v)} className="mt-1.5 space-y-1.5">
              {hasShares && <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="replace" data-testid="owner-move-shares-replace" />Bisherigen Eigentümer in den Quoten durch den neuen ersetzen</label>}
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="keep" data-testid="owner-move-shares-keep" />Quoten unverändert lassen</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="new100" data-testid="owner-move-shares-new100" />Neuer Eigentümer hält 100 %</label>
            </RadioGroup>
          </div>
          <div>
            <Label>Notiz (optional)</Label>
            <Textarea rows={2} value={f.note || ""} onChange={(e) => set("note", e.target.value)} placeholder="z. B. Kaufvertrag vom …" data-testid="owner-move-note" />
          </div>
          <PreviewBlock plan={plan} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={apply} disabled={!plan || busy} className="bg-slate-900 hover:bg-slate-800" data-testid="owner-move-apply">
            {busy ? "Führe aus…" : "Umzug durchführen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
